/**
 * MainLayout Component
 *
 * Componente de layout principal que gerencia a margem dinâmica
 * do conteúdo baseado no estado de colapso da sidebar.
 *
 * UX/UI Design:
 * - Sidebar colapsada (64px): conteúdo usa ml-16 (64px margin)
 * - Sidebar expandida (256px): conteúdo usa ml-64 (256px margin)
 * - Transição suave: 300ms com cubic-bezier para naturalidade
 * - Background adaptativo: light/dark mode
 *
 * Benefícios:
 * - DRY: Uma única fonte de verdade para layout
 * - Performance: Zero overhead, apenas CSS condicional
 * - Manutenibilidade: Mudanças centralizadas
 * - Acessibilidade: Preserva estrutura semântica
 * - Ganho de espaço: 192px quando sidebar colapsada
 *
 * @example
 * <MainLayout>
 *   <Header title="Dashboard" />
 *   <div className="p-6">
 *     <h1>Conteúdo da Página</h1>
 *   </div>
 * </MainLayout>
 */
import { ReactNode } from 'react';
import { useSidebar } from '../contexts/SidebarContext';
import { SimulacaoBanner } from './SimulacaoBanner';

interface MainLayoutProps {
  /** Conteúdo da página (Header + corpo) */
  children: ReactNode;
  /** Classes CSS adicionais (opcional) */
  className?: string;
}

export const MainLayout = ({ children, className = '' }: MainLayoutProps) => {
  const { isCollapsed } = useSidebar();

  return (
    <div
      className={`
        min-h-screen
        bg-gray-50 dark:bg-gray-950
        transition-all duration-300 ease-in-out
        ${isCollapsed ? 'ml-16' : 'ml-64'}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      style={{
        // Sincronizar timing com Sidebar.tsx linha 103
        transitionProperty: 'margin-left',
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <SimulacaoBanner />
      {children}
    </div>
  );
};
